import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MainLayout } from '@/components/layout/MainLayout';
import { PageHeader } from '@/components/layout/PageHeader';
import { Textarea } from '@/components/ui/textarea';
import { StatusBadgeOS } from '@/components/ordens/StatusBadgeOS';
import { HistoricoEventosOS } from '@/components/ordens/HistoricoEventosOS';
import { OrdemServicoFormDialog } from '@/components/ordens/OrdemServicoFormDialog';
import { ItensOSDisplay } from '@/components/ordens/ItensOSDisplay';
import { ReagendarServicoDialog } from '@/components/ordens/ReagendarServicoDialog';
import { FinalizarServicoDialog } from '@/components/ordens/FinalizarServicoDialog';
import { ExcluirOSDialog } from '@/components/ordens/ExcluirOSDialog';
import { CancelarOSDialog } from '@/components/ordens/CancelarOSDialog';
import { ReabrirOSDialog } from '@/components/ordens/ReabrirOSDialog';
import {
  useOrdemServico,
  useAceitarOS,
  useRecusarOS,
  useIniciarExecucaoOS,
  useAdicionarObservacaoOS,
  useAutoatribuirOS,
} from '@/hooks/useOrdensServico';
import { useEventosOS } from '@/hooks/useEventosOS';
import { getReaberturaAtiva } from '@/lib/os-reabertura';
import { ReaberturaBanner } from '@/components/ordens/ReaberturaBanner';
import { useAuthContext } from '@/contexts/AuthContext';
import { formatarEndereco } from '@/types/ordemServico';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  ArrowLeft,
  Calendar,
  MapPin,
  Phone,
  User,
  Loader2,
  Edit,
  CheckCircle,
  XCircle,
  Play,
  Ban,
  CalendarClock,
  Trash2,
  RotateCcw,
  Clock,
  Timer,
  Mail,
  Repeat,
} from 'lucide-react';

function formatarTempo(minutos: number): string {
  if (minutos < 60) return `${minutos} minuto${minutos !== 1 ? 's' : ''}`;
  const horas = Math.floor(minutos / 60);
  const mins = minutos % 60;
  if (mins === 0) return `${horas} hora${horas !== 1 ? 's' : ''}`;
  return `${horas}h ${mins}min`;
}

interface SectionProps {
  titulo: React.ReactNode;
  children: React.ReactNode;
  compact?: boolean;
}
function Section({ titulo, children, compact = false }: SectionProps) {
  return (
    <div className="ow-card">
      <div className="px-5 pt-4 pb-2">
        <h3 className={`font-bold tracking-tight ${compact ? 'text-sm' : 'text-base'}`}>
          {titulo}
        </h3>
      </div>
      <div className="px-5 pb-4">{children}</div>
    </div>
  );
}

export default function OrdemServicoDetalhes() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isPrestador, isAdmin, isAtendente } = useAuthContext();

  const { data: os, isLoading } = useOrdemServico(id);
  const { data: eventosOS } = useEventosOS(id);

  const aceitarOS = useAceitarOS();
  const recusarOS = useRecusarOS();
  const iniciarOS = useIniciarExecucaoOS();
  const adicionarObs = useAdicionarObservacaoOS();
  const autoatribuirOS = useAutoatribuirOS();

  const [dialogEditar, setDialogEditar] = useState(false);
  const [dialogCancelar, setDialogCancelar] = useState(false);
  const [dialogReagendar, setDialogReagendar] = useState(false);
  const [dialogFinalizar, setDialogFinalizar] = useState(false);
  const [dialogExcluir, setDialogExcluir] = useState(false);
  const [dialogReabrir, setDialogReabrir] = useState(false);
  const [novaObservacao, setNovaObservacao] = useState('');

  if (isLoading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-[50vh]">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </MainLayout>
    );
  }

  if (!os) {
    return (
      <MainLayout>
        <div className="p-6 text-center flex flex-col items-center gap-3">
          <h2 className="text-xl font-semibold">
            {t('orders.details.notFound', 'Ordem de serviço não encontrada')}
          </h2>
          <button
            type="button"
            className="ow-btn ow-btn--outline ow-btn--sm"
            onClick={() => navigate('/ordens')}
          >
            <ArrowLeft className="h-4 w-4" />
            {t('common.back')}
          </button>
        </div>
      </MainLayout>
    );
  }

  const handleAdicionarObservacao = async () => {
    if (!novaObservacao.trim()) return;
    await adicionarObs.mutateAsync({ id: os.id, observacao: novaObservacao.trim() });
    setNovaObservacao('');
  };

  const podeEditar = isAdmin || (isAtendente && os.responsavel_id);
  const podeCancelar = (isAdmin || isAtendente) && !['concluida', 'cancelada'].includes(os.status);
  const isOSDisponivel = os.status === 'pendente' && !os.prestador_id;
  const isOSPendenteComPrestador = os.status === 'pendente' && !!os.prestador_id;
  const podeAceitarDisponivel = isPrestador && isOSDisponivel;
  const podeAceitarAtribuida = isPrestador && isOSPendenteComPrestador;
  const podeRecusar = isPrestador && isOSPendenteComPrestador;
  const podeIniciar = isPrestador && os.status === 'aceita';
  const podeFinalizar = (isPrestador || isAdmin) && os.status === 'em_execucao';
  const podeReagendar =
    (isAdmin || isAtendente) && !['concluida', 'cancelada', 'recusada'].includes(os.status);
  const podeExcluir = isAdmin || isAtendente;
  const podeReabrir = (isAdmin || isAtendente) && ['concluida', 'cancelada'].includes(os.status);

  const reaberturaAtiva = getReaberturaAtiva(eventosOS, os.status);

  // Status efetivo (alinhado com o Kanban): pendente sem prestador =
  // "Disponível"; pendente com prestador = "Aceita". Restantes inalterados.
  const statusEfetivo =
    os.status === 'pendente' ? (os.prestador_id ? 'aceita' : 'disponiveis') : os.status;

  const temAcoes =
    podeAceitarDisponivel ||
    podeAceitarAtribuida ||
    podeRecusar ||
    podeIniciar ||
    podeFinalizar ||
    podeReagendar ||
    podeEditar ||
    podeCancelar ||
    podeExcluir ||
    podeReabrir;

  return (
    <MainLayout>
      <div className="p-6 max-w-4xl mx-auto flex flex-col gap-5">
        <PageHeader
          titulo={
            <span className="inline-flex items-center gap-3 flex-wrap">
              <span className="font-mono">{os.numero_os}</span>
              <StatusBadgeOS status={statusEfetivo} />
            </span>
          }
          subtitulo={`${t('orders.details.createdLabel', 'Criada em')} ${format(
            new Date(os.criado_em),
            "dd/MM/yyyy 'às' HH:mm",
            { locale: ptBR },
          )}`}
          acoes={
            <button
              type="button"
              className="ow-btn ow-btn--ghost ow-btn--sm"
              onClick={() => navigate('/ordens')}
            >
              <ArrowLeft size={14} />
              {t('common.back')}
            </button>
          }
        />

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {/* Coluna principal */}
          <div className="lg:col-span-2 flex flex-col gap-5">
            {reaberturaAtiva && <ReaberturaBanner reabertura={reaberturaAtiva} />}
            <ItensOSDisplay
              ordemServicoId={os.id}
              valorTotal={os.valor_total}
              status={os.status}
              dataInicioExecucao={os.data_inicio_execucao}
            />

            {/* Detalhes */}
            <Section titulo={t('orders.details.title', 'Detalhes')}>
              <div className="flex flex-col gap-4">
                {os.recorrencia_id && (
                  <div className="flex items-start gap-3">
                    <Repeat className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-sm">
                        {t('orders.details.recurringTitle', 'Recorrência')}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {t('recurrences.badge')}
                      </p>
                    </div>
                  </div>
                )}

                <div className="flex items-start gap-3">
                  <Calendar className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-sm">{t('orders.form.scheduledDate')}</p>
                    <p className="text-sm text-muted-foreground">
                      {os.data_agendada
                        ? format(new Date(os.data_agendada), "dd/MM/yyyy 'às' HH:mm", {
                            locale: ptBR,
                          })
                        : t('orders.details.noDate', 'Não agendada')}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <MapPin className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-sm">{t('orders.form.serviceAddress')}</p>
                    <p className="text-sm text-muted-foreground whitespace-pre-line">
                      {formatarEndereco(os.endereco, t)}
                    </p>
                  </div>
                </div>

                {(os.status === 'em_execucao' || os.status === 'concluida') &&
                  os.data_inicio_execucao && (
                    <div className="flex items-start gap-3">
                      <Timer className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="font-medium text-sm">
                          {t('orders.details.executionTime', 'Tempo de execução')}
                        </p>
                        {os.status === 'concluida' && os.tempo_servico_minutos ? (
                          <span className="status-pill mt-1" style={{ padding: '2px 10px' }}>
                            <Clock className="h-3 w-3" />
                            {formatarTempo(os.tempo_servico_minutos)}
                          </span>
                        ) : (
                          <p className="text-sm text-muted-foreground">
                            {t('orders.details.startedAt', 'Iniciado em')}{' '}
                            {format(new Date(os.data_inicio_execucao), "dd/MM/yyyy 'às' HH:mm", {
                              locale: ptBR,
                            })}
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                {os.status === 'concluida' && os.data_fim_execucao && (
                  <div className="flex items-start gap-3">
                    <CheckCircle
                      className="h-4 w-4 mt-0.5 flex-shrink-0"
                      style={{ color: '#22C55E' }}
                    />
                    <div>
                      <p className="font-medium text-sm">
                        {t('orders.details.finishedAt', 'Finalizado em')}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {format(new Date(os.data_fim_execucao), "dd/MM/yyyy 'às' HH:mm", {
                          locale: ptBR,
                        })}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </Section>

            {/* Observações */}
            <Section titulo={t('common.notes', 'Observações')}>
              <div className="flex flex-col gap-3">
                {os.observacoes ? (
                  <div className="whitespace-pre-wrap text-sm bg-muted/40 p-3 rounded-lg">
                    {os.observacoes}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    {t('orders.details.noNotes', 'Nenhuma observação.')}
                  </p>
                )}
                <div className="flex flex-col gap-2">
                  <Textarea
                    placeholder={t('orders.details.addNote', 'Adicionar observação...')}
                    value={novaObservacao}
                    onChange={(e) => setNovaObservacao(e.target.value)}
                    rows={2}
                  />
                  <button
                    type="button"
                    className="ow-btn ow-btn--primary ow-btn--sm self-start"
                    onClick={handleAdicionarObservacao}
                    disabled={!novaObservacao.trim() || adicionarObs.isPending}
                  >
                    {adicionarObs.isPending && <Loader2 size={14} className="animate-spin" />}
                    {t('common.add', 'Adicionar')}
                  </button>
                </div>
              </div>
            </Section>

            {/* Histórico */}
            <Section titulo={t('orders.details.history', 'Histórico')}>
              <HistoricoEventosOS ordemServicoId={os.id} />
            </Section>
          </div>

          {/* Coluna lateral */}
          <div className="flex flex-col gap-5">
            {/* Cliente */}
            <Section titulo={t('orders.form.client', 'Cliente')} compact>
              {os.cliente ? (
                <div className="flex flex-col gap-2 text-sm">
                  {os.cliente.codigo_cliente && (
                    <span className="mono text-xs text-muted-foreground">
                      {os.cliente.codigo_cliente}
                    </span>
                  )}
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">{os.cliente.nome || t('common.noName')}</span>
                  </div>
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Phone className="h-4 w-4" />
                    <span className="mono text-xs">{os.cliente.telefone}</span>
                  </div>
                  {os.cliente.email && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Mail className="h-4 w-4" />
                      <span className="text-xs truncate">{os.cliente.email}</span>
                    </div>
                  )}
                  <button
                    type="button"
                    className="ow-btn ow-btn--outline ow-btn--sm ow-btn--block mt-1"
                    onClick={() =>
                      navigate(
                        isPrestador
                          ? `/cliente/${os.cliente?.id}`
                          : `/cliente-potential/${os.cliente?.id}`,
                      )
                    }
                  >
                    {t('orders.details.viewClient', 'Ver Cliente')}
                  </button>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  {t('orders.details.noClient', 'Cliente não associado')}
                </p>
              )}
            </Section>

            {/* Prestador */}
            <Section titulo={t('orders.details.providerTitle', 'Prestador')} compact>
              {os.prestador?.profile ? (
                <div className="flex flex-col gap-2 text-sm">
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">{os.prestador.profile.nome}</span>
                  </div>
                  {os.prestador.profile.telefone && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Phone className="h-4 w-4" />
                      <span className="mono text-xs">{os.prestador.profile.telefone}</span>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  {t('orders.form.notAssigned', 'Não atribuído')}
                </p>
              )}
            </Section>

            {/* Responsável */}
            <Section titulo={t('orders.details.responsible', 'Responsável')} compact>
              {os.responsavel ? (
                <div className="flex items-center gap-2 text-sm">
                  <User className="h-4 w-4 text-muted-foreground" />
                  <span className="font-medium">{os.responsavel.nome}</span>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  {t('orders.form.notAssigned', 'Não atribuído')}
                </p>
              )}
            </Section>

            {/* Ações */}
            {temAcoes && (
              <Section titulo={t('orders.details.actions', 'Ações')} compact>
                <div className="flex flex-col gap-2">
                  {podeAceitarDisponivel && (
                    <button
                      type="button"
                      className="ow-btn ow-btn--primary ow-btn--block ow-btn--sm"
                      onClick={() => autoatribuirOS.mutate(os.id)}
                      disabled={autoatribuirOS.isPending}
                    >
                      <CheckCircle size={14} />
                      {t('orders.actions.accept', 'Aceitar Serviço')}
                    </button>
                  )}
                  {podeAceitarAtribuida && (
                    <button
                      type="button"
                      className="ow-btn ow-btn--primary ow-btn--block ow-btn--sm"
                      onClick={() => aceitarOS.mutate(os.id)}
                      disabled={aceitarOS.isPending}
                    >
                      <CheckCircle size={14} />
                      {t('orders.actions.accept', 'Aceitar Serviço')}
                    </button>
                  )}
                  {podeRecusar && (
                    <button
                      type="button"
                      className="ow-btn ow-btn--danger ow-btn--block ow-btn--sm"
                      onClick={() => recusarOS.mutate({ id: os.id })}
                      disabled={recusarOS.isPending}
                    >
                      <XCircle size={14} />
                      {t('orders.actions.refuse', 'Recusar')}
                    </button>
                  )}
                  {podeIniciar && (
                    <button
                      type="button"
                      className="ow-btn ow-btn--primary ow-btn--block ow-btn--sm"
                      onClick={() => iniciarOS.mutate(os.id)}
                      disabled={iniciarOS.isPending}
                    >
                      <Play size={14} />
                      {t('orders.actions.start', 'Iniciar Execução')}
                    </button>
                  )}
                  {podeFinalizar && (
                    <button
                      type="button"
                      className="ow-btn ow-btn--primary ow-btn--block ow-btn--sm"
                      onClick={() => setDialogFinalizar(true)}
                    >
                      <CheckCircle size={14} />
                      {t('orders.actions.finalize', 'Finalizar Serviço')}
                    </button>
                  )}
                  {podeReagendar && (
                    <button
                      type="button"
                      className="ow-btn ow-btn--outline ow-btn--block ow-btn--sm"
                      onClick={() => setDialogReagendar(true)}
                    >
                      <CalendarClock size={14} />
                      {t('orders.actions.reschedule', 'Reagendar Serviço')}
                    </button>
                  )}
                  {podeEditar && (
                    <button
                      type="button"
                      className="ow-btn ow-btn--outline ow-btn--block ow-btn--sm"
                      onClick={() => setDialogEditar(true)}
                    >
                      <Edit size={14} />
                      {t('orders.actions.edit', 'Editar OS')}
                    </button>
                  )}
                  {(podeCancelar || podeExcluir) && (
                    <div className="border-t border-border-soft my-1" />
                  )}
                  {podeCancelar && (
                    <button
                      type="button"
                      className="ow-btn ow-btn--danger ow-btn--block ow-btn--sm"
                      onClick={() => setDialogCancelar(true)}
                    >
                      <Ban size={14} />
                      {t('orders.actions.cancel', 'Cancelar OS')}
                    </button>
                  )}
                  {podeExcluir && (
                    <button
                      type="button"
                      className="ow-btn ow-btn--danger ow-btn--block ow-btn--sm"
                      onClick={() => setDialogExcluir(true)}
                    >
                      <Trash2 size={14} />
                      {t('orders.actions.delete', 'Excluir OS')}
                    </button>
                  )}
                  {podeReabrir && (
                    <>
                      <div className="border-t border-border-soft my-1" />
                      <button
                        type="button"
                        className="ow-btn ow-btn--primary ow-btn--block ow-btn--sm"
                        onClick={() => setDialogReabrir(true)}
                      >
                        <RotateCcw size={14} />
                        {t('orders.actions.reopen', 'Reabrir OS')}
                      </button>
                    </>
                  )}
                </div>
              </Section>
            )}
          </div>
        </div>
      </div>

      <OrdemServicoFormDialog open={dialogEditar} onOpenChange={setDialogEditar} osParaEditar={os} />
      <ReagendarServicoDialog
        open={dialogReagendar}
        onOpenChange={setDialogReagendar}
        osId={os.id}
        numeroOs={os.numero_os}
        dataAtual={os.data_agendada}
      />
      <FinalizarServicoDialog
        open={dialogFinalizar}
        onOpenChange={setDialogFinalizar}
        osId={os.id}
        numeroOs={os.numero_os}
      />
      <ExcluirOSDialog open={dialogExcluir} onOpenChange={setDialogExcluir} os={os} />
      <ReabrirOSDialog open={dialogReabrir} onOpenChange={setDialogReabrir} os={os} />
      <CancelarOSDialog open={dialogCancelar} onOpenChange={setDialogCancelar} os={os} />
    </MainLayout>
  );
}
